import type { Validator } from '@seek/validators-js';
import type { Resolver, FieldValues } from 'react-hook-form';

import type { Language } from 'src/types';

interface ValidationError {
  message: string;
  fieldName: string;
  rule: string;
}
interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
}

type Rules<Fields extends Record<string, unknown>> = {
  [Key in keyof Fields]: {
    validators: Array<Validator<any, any>>;
    validatorProps?: Record<string, any>;
  };
};

let language: Language = 'en';
export const setLanguage = (lang: Language): void => {
  language = lang;
};

const runValidation = async <Fields extends Record<string, unknown>>({
  fields,
  rules,
}: {
  fields: Fields;
  rules: Rules<Fields>;
}): Promise<ValidationResult> => {
  const errorsPromises = Object.entries(rules)
    .flatMap(([fieldName, { validators, validatorProps }]) =>
      validators.map((validator) => ({
        fieldName,
        validator,
        validatorProps,
        fieldProps: {
          value: fields[fieldName],
          validatorProps: validatorProps ?? {},
          formValues: fields,
        },
      })),
    )
    .filter(({ validator, fieldProps }) => !validator.test(fieldProps))
    .map(async ({ fieldName, validator, fieldProps }) => ({
      // casting to 'en' as validator.getErrorMessage doesn't accept all lanaguages yet
      message: await validator.getErrorMessage(fieldProps, language as 'en'),
      fieldName,
      rule: validator.name,
    }));

  const errors = await Promise.all(errorsPromises);

  return { valid: errors.length === 0, errors };
};

export const seekResolver =
  <T extends FieldValues>(
    schema: Parameters<typeof runValidation>[0]['rules'],
  ): Resolver<T, any, T> =>
  async (data: T) => {
    const { valid, errors } = await runValidation({
      fields: data,
      rules: schema,
    });

    if (!valid) {
      return {
        values: {} as T, // Type assertion needed here
        errors: errors.reduce(
          (acc, { fieldName, message, rule }) => ({
            ...acc,
            [fieldName]: { message, type: rule },
          }),
          {},
        ),
      };
    }

    return { values: data, errors: {} };
  };
